import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { Loader2, ShieldCheck, RefreshCw } from 'lucide-react';
import ChangePasswordExpiredDialog from '../components/ChangePasswordExpiredDialog';
import api from '../utils/api';

const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [passwordExpired, setPasswordExpired] = useState(false);
  const [expiredUserInfo, setExpiredUserInfo] = useState(null);
  
  // 2FA States
  const [requires2FA, setRequires2FA] = useState(false);
  const [twoFAUserId, setTwoFAUserId] = useState(null);
  const [twoFAUserEmail, setTwoFAUserEmail] = useState(null);
  const [twoFACode, setTwoFACode] = useState('');
  const [verifying2FA, setVerifying2FA] = useState(false);
  const [resending2FA, setResending2FA] = useState(false);
  
  const { login, setAuth } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const result = await login(email, password);
      
      // Verificar si requiere 2FA
      if (result?.requires_2fa) {
        setRequires2FA(true);
        setTwoFAUserId(result.user_id);
        setTwoFAUserEmail(result.user_email);
        toast.info(result.message || 'Código de verificación enviado a tu correo');
        return;
      }
      
      // Verificar si la contraseña expiró
      if (result?.password_expired) {
        setPasswordExpired(true);
        setExpiredUserInfo({
          userId: result.user_id,
          userEmail: result.user_email,
          expirationInfo: result.password_info
        });
        toast.error('Su contraseña ha expirado. Debe cambiarla para continuar.');
      } else {
        // Login exitoso
        toast.success('¡Bienvenido!');
        
        // Mostrar advertencia si la contraseña está próxima a expirar
        if (result?.user?.password_warning) {
          setTimeout(() => {
            toast.warning(result.user.password_warning, {
              duration: 8000
            });
          }, 1000);
        }
        
        navigate('/dashboard');
      }
    } catch (error) {
      const status = error.response?.status;
      const detail = error.response?.data?.detail || '';

      if (status === 429) {
        toast.error(detail || 'Cuenta bloqueada temporalmente por múltiples intentos fallidos.', { duration: 6000 });
      } else if (status === 403) {
        toast.error(detail || 'Su cuenta se encuentra inactiva. Comuníquese con el Área de Tecnología.', { duration: 6000 });
      } else {
        toast.error('Error usuario o contraseña inválidos. Si has olvidado tus datos comunícate con el Área de Tecnología.', { 
          duration: 6000 
        });
      }
    } finally {
      setLoading(false);
    }
  };

  const handleVerify2FA = async (e) => {
    e.preventDefault();
    if (!twoFACode || twoFACode.length !== 6) {
      toast.error('Ingrese el código de 6 dígitos');
      return;
    }
    
    setVerifying2FA(true);
    try {
      const response = await api.post('/auth/verify-2fa', {
        user_id: twoFAUserId,
        code: twoFACode
      });
      
      // Login exitoso después de 2FA
      const { access_token, user } = response.data;
      localStorage.setItem('token', access_token);
      setAuth({ isAuthenticated: true, user, token: access_token });
      
      toast.success('¡Bienvenido!');
      navigate('/dashboard');
    } catch (error) {
      const message = error.response?.data?.detail || 'Código incorrecto';
      toast.error(message);
    } finally {
      setVerifying2FA(false);
    }
  };

  const handleResend2FA = async () => {
    setResending2FA(true);
    try {
      await api.post('/auth/resend-2fa', { user_id: twoFAUserId });
      toast.success('Nuevo código enviado a tu correo');
      setTwoFACode('');
    } catch (error) {
      toast.error('Error al reenviar el código');
    } finally {
      setResending2FA(false);
    }
  };

  const handleCancel2FA = () => {
    setRequires2FA(false);
    setTwoFAUserId(null);
    setTwoFAUserEmail(null);
    setTwoFACode('');
  };

  const handlePasswordChangeSuccess = () => {
    setPasswordExpired(false);
    setExpiredUserInfo(null);
    setPassword('');
    toast.success('Contraseña actualizada. Por favor, inicie sesión nuevamente.');
  };

  // Renderizar pantalla de verificación 2FA
  if (requires2FA) {
    return (
      <div className="min-h-screen flex" data-testid="twofa-page">
        <div className="flex-1 flex items-center justify-center bg-white p-4 sm:p-6 lg:p-8">
          <div className="w-full max-w-md">
            <div className="mb-6 sm:mb-8 text-center">
              <div className="flex justify-center mb-4">
                <div className="w-16 h-16 bg-[#9d8272]/10 rounded-full flex items-center justify-center">
                  <ShieldCheck className="w-8 h-8 text-[#9d8272]" />
                </div>
              </div>
              <h1 className="text-2xl sm:text-3xl font-bold text-[#2D2D2D] mb-2" style={{ fontFamily: 'Playfair Display, serif' }}>
                Verificación de Seguridad
              </h1>
              <p className="text-sm sm:text-base text-[#5A5A5A]">
                Ingresa el código de 6 dígitos enviado a
              </p>
              <p className="text-sm font-semibold text-[#9d8272]">{twoFAUserEmail}</p>
            </div>

            <form onSubmit={handleVerify2FA} className="space-y-4 sm:space-y-6" data-testid="twofa-form">
              <div>
                <Label htmlFor="code" className="text-[#2D2D2D] font-medium sr-only">Código de Verificación</Label>
                <Input
                  id="code"
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  maxLength={6}
                  value={twoFACode}
                  onChange={(e) => setTwoFACode(e.target.value.replace(/\D/g, ''))}
                  className="mt-2 border-[#c5b8a2] focus:ring-[#9d8272] text-center text-2xl tracking-[0.5em] font-mono h-14"
                  placeholder="000000"
                  autoComplete="one-time-code"
                  autoFocus
                  data-testid="twofa-code-input"
                />
              </div>

              <Button
                type="submit"
                disabled={verifying2FA || twoFACode.length !== 6}
                className="w-full bg-[#9d8272] hover:bg-[#8a7162] text-white font-medium py-3 rounded-md transition-all shadow-sm hover:shadow-md"
                data-testid="twofa-verify-button"
              >
                {verifying2FA ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Verificando...
                  </>
                ) : (
                  'Verificar Código'
                )}
              </Button>

              <div className="flex items-center justify-between pt-2">
                <Button
                  type="button"
                  variant="ghost"
                  onClick={handleCancel2FA}
                  className="text-[#5A5A5A] hover:text-[#2D2D2D]"
                >
                  Cancelar
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  onClick={handleResend2FA}
                  disabled={resending2FA}
                  className="text-[#9d8272] hover:text-[#8a7162]"
                  data-testid="twofa-resend-button"
                >
                  {resending2FA ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Enviando...
                    </>
                  ) : (
                    <>
                      <RefreshCw className="w-4 h-4 mr-2" />
                      Reenviar código
                    </>
                  )}
                </Button>
              </div>
            </form>

            <div className="mt-6 sm:mt-8 p-3 sm:p-4 bg-[#f8f8f8] rounded-lg border border-[#E8E8E7]">
              <p className="text-xs sm:text-sm text-[#5A5A5A]">
                <strong>💡 Consejo:</strong> El código expira en 10 minutos. 
                Revisa tu bandeja de entrada y carpeta de spam.
              </p>
            </div>
          </div>
        </div>

        {/* Right side - Image with Overlay */}
        <div
          className="hidden lg:flex flex-1 relative bg-cover bg-center"
          style={{
            backgroundImage: "url('https://images.unsplash.com/photo-1758448511648-d7d8e1993c3f?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzh8MHwxfHNlYXJjaHwyfHxtb2Rlcm4lMjBsdXh1cnklMjBhcGFydG1lbnQlMjBidWlsZGluZyUyMGV4dGVyaW9yJTIwYXJjaGl0ZWN0dXJlfGVufDB8fHx8MTc2NzA2NzE1M3ww&ixlib=rb-4.1.0&q=85')",
          }}
        >
          <div
            className="absolute inset-0"
            style={{
              background: 'linear-gradient(to right, rgba(157, 130, 114, 0.9), rgba(140, 146, 111, 0.8))'
            }}
          >
            <div className="flex items-center justify-center h-full p-12">
              <div className="text-white max-w-lg text-center">
                <ShieldCheck className="w-20 h-20 mx-auto mb-6 opacity-80" />
                <h2 className="text-4xl font-bold mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
                  Autenticación de Dos Factores
                </h2>
                <p className="text-lg leading-relaxed opacity-90">
                  Protegemos tu cuenta con una capa adicional de seguridad. 
                  Hemos enviado un código único a tu correo electrónico.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex" data-testid="login-page">
      {/* Left side - Login Form */}
      <div className="flex-1 flex items-center justify-center bg-white p-4 sm:p-6 lg:p-8">
        <div className="w-full max-w-md">
          <div className="mb-6 sm:mb-8">
            <h1 className="text-3xl sm:text-4xl font-bold text-[#9d8272] mb-2" style={{ fontFamily: 'Playfair Display, serif' }}>
              IMAGINA
            </h1>
            <p className="text-base sm:text-lg text-[#5A5A5A]">Sistema de Postventa</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4 sm:space-y-6" data-testid="login-form">
            <div>
              <Label htmlFor="email" className="text-[#2D2D2D] font-medium">Correo Electrónico</Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="mt-2 border-[#c5b8a2] focus:ring-[#9d8272]" 
                placeholder="tu@correo.com"
                data-testid="email-input"
              />
            </div>

            <div>
              <Label htmlFor="password" className="text-[#2D2D2D] font-medium">Contraseña</Label>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                className="mt-2 border-[#c5b8a2] focus:ring-[#9d8272]"
                placeholder="••••••••"
                data-testid="password-input"
              />
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="w-full bg-[#9d8272] hover:bg-[#8a7162] text-white font-medium py-3 rounded-md transition-all shadow-sm hover:shadow-md"
              data-testid="login-submit-button"
            >
              {loading ? 'Iniciando sesión...' : 'Iniciar Sesión'}
            </Button>
          </form>
        </div>
      </div>

      {/* Right side - Image with Overlay */}
      <div
        className="hidden lg:flex flex-1 relative bg-cover bg-center"
        style={{
          backgroundImage: "url('https://images.unsplash.com/photo-1758448511648-d7d8e1993c3f?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzh8MHwxfHNlYXJjaHwyfHxtb2Rlcm4lMjBsdXh1cnklMjBhcGFydG1lbnQlMjBidWlsZGluZyUyMGV4dGVyaW9yJTIwYXJjaGl0ZWN0dXJlfGVufDB8fHx8MTc2NzA2NzE1M3ww&ixlib=rb-4.1.0&q=85')",
        }}
      >
        <div
          className="absolute inset-0"
          style={{
            background: 'linear-gradient(to right, rgba(157, 130, 114, 0.9), rgba(140, 146, 111, 0.8))'
          }}
        >
          <div className="flex items-center justify-center h-full p-12">
            <div className="text-white max-w-lg">
              <h2 className="text-5xl font-bold mb-6" style={{ fontFamily: 'Playfair Display, serif' }}>
                Gestión de Postventa Inmobiliaria
              </h2>
              <p className="text-xl leading-relaxed">
                Administra solicitudes, supervisa proyectos y garantiza la satisfacción de tus clientes con nuestra plataforma integral.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Dialog de cambio de contraseña expirada */}
      {passwordExpired && expiredUserInfo && (
        <ChangePasswordExpiredDialog
          open={passwordExpired}
          userId={expiredUserInfo.userId}
          userEmail={expiredUserInfo.userEmail}
          expirationInfo={expiredUserInfo.expirationInfo}
          onSuccess={handlePasswordChangeSuccess}
        />
      )}
    </div>
  );
};

export default Login;
